import app from 'flarum/forum/app';
import { extend, override } from 'flarum/common/extend';
import DiscussionHero from 'flarum/forum/components/DiscussionHero';
import DiscussionPage from 'flarum/forum/components/DiscussionPage';
import CommentPost from 'flarum/forum/components/CommentPost';
import Link from 'flarum/common/components/Link';
import Button from 'flarum/common/components/Button';
import Dropdown from 'flarum/common/components/Dropdown';
import Separator from 'flarum/common/components/Separator';
import DiscussionControls from 'flarum/forum/utils/DiscussionControls';
import Breadcrumbs from './components/Breadcrumbs';
import OriginalPostAuthor from './components/OriginalPostAuthor';
import classList from 'flarum/common/utils/classList';
import Icon from 'flarum/common/components/Icon';
import username from 'flarum/common/helpers/username';
import punctuateSeries from 'flarum/common/helpers/punctuateSeries';
import { discussionPlacement, subsections } from './utils/sections';
import { visibleNumber } from './utils/postNumbers';
export default function addDiscussionPage() {
  // Head like a portal article. The crumbs run across the page (core's hero slot); the head of the
  // topic — section eyebrow (core tag labels), badges, title — opens the main column, so the
  // sidebar and the column divider start level with it. Core's DiscussionHero is reused as is,
  // only rendered at the top of the page content instead of in the hero slot. The author signs
  // the opening post below its text (see the CommentPost view below).
  override(DiscussionPage.prototype, 'hero', function () {
    const discussion = this.discussion;
    const {
      section,
      subsection
    } = discussionPlacement(discussion.tags() || []);
    const crumbs = [section, subsection].filter(Boolean).map(tag => ({
      label: tag.name(),
      href: app.route.tag(tag)
    }));
    return m("div", {
      className: "TpDiscussionTop"
    }, m("div", {
      className: "container"
    }, m(Breadcrumbs, {
      items: [...crumbs, {
        label: discussion.title()
      }]
    })));
  });
  override(DiscussionPage.prototype, 'view', function (original) {
    for (var _len = arguments.length, args = new Array(_len > 1 ? _len - 1 : 0), _key = 1; _key < _len; _key++) {
      args[_key - 1] = arguments[_key];
    }
    const vnode = original(...args);
    if (this.discussion && !this.loading && vnode && Array.isArray(vnode.children)) {
      vnode.children = [m(DiscussionHero, {
        discussion: this.discussion
      }), ...vnode.children];
    }
    return vnode;
  });

  // The opening post of the discussion being read: the topic's body under the title, its actions,
  // the author's signature, then a hairline and "ОТВЕТЫ · N" (Flarum's replyCount()).
  // Only inside the discussion's own stream — post #1 also appears in profile activity lists.
  const isOpeningPost = post => {
    const discussion = post.discussion();
    return post.number() === 1 && !!discussion && app.current.matches(DiscussionPage) && app.current.get('discussion') === discussion;
  };
  extend(CommentPost.prototype, 'elementAttrs', function (attrs) {
    if (isOpeningPost(this.attrs.post)) attrs.className = classList(attrs.className, 'TpOriginalPost');
  });
  override(CommentPost.prototype, 'view', function (original) {
    for (var _len2 = arguments.length, args = new Array(_len2 > 1 ? _len2 - 1 : 0), _key2 = 1; _key2 < _len2; _key2++) {
      args[_key2 - 1] = arguments[_key2];
    }
    const vnode = original(...args);
    const post = this.attrs.post;
    if (!isOpeningPost(post)) return vnode;
    return [vnode, post.createdAt() ? m(OriginalPostAuthor, {
      post: post
    }) : null, m("p", {
      className: "TpRepliesHead TpEyebrow",
      role: "heading",
      "aria-level": 2
    }, app.translator.trans('tarotportal-theme.forum.discussion.replies_heading', {
      count: post.discussion().replyCount() || 0
    }))];
  });

  // A soft-deleted post collapses to one line: author, time, "Удалено", a text toggle for its
  // content (core's is a second "•••" next to the controls' "•••"), controls, number.
  extend(CommentPost.prototype, 'headerItems', function (items) {
    const post = this.attrs.post;
    if (!post.isHidden()) return;
    items.add('tp-hidden', m("span", {
      className: "TpHiddenLabel"
    }, app.translator.trans('tarotportal-theme.forum.discussion.hidden_label')), -10);
    if (items.has('toggle')) {
      items.setContent('toggle', m(Button, {
        className: "Button Button--link TpHiddenToggle",
        onclick: this.toggleContent.bind(this),
        "aria-expanded": this.revealContent ? 'true' : 'false'
      }, app.translator.trans(this.revealContent ? 'tarotportal-theme.forum.discussion.hidden_hide' : 'tarotportal-theme.forum.discussion.hidden_show')));
      items.setPriority('toggle', -20);
    }
  });

  // Images in posts open in fof/photoswipe's lightbox (installed separately); only its behaviour
  // is tuned here: a click on the picture itself never closes it, a quiet fade, no counter.
  extend(CommentPost.prototype, 'oninit', function () {
    const lightbox = this.lightbox;
    if (!lightbox?.options) return;
    Object.assign(lightbox.options, {
      bgOpacity: 0.94,
      showHideAnimationType: 'fade',
      showAnimationDuration: 180,
      hideAnimationDuration: 160,
      imageClickAction: 'zoom',
      tapAction: 'toggle-controls',
      bgClickAction: 'close',
      counter: false,
      zoom: false,
      // ~92vw × ~90vh on wide screens; nearly edge to edge on phones
      paddingFn: viewport => {
        const phone = viewport.x < 768;
        const x = phone ? 8 : Math.round(viewport.x * 0.04);
        const y = phone ? 56 : Math.round(viewport.y * 0.05);
        return {
          top: y,
          bottom: y,
          left: x,
          right: x
        };
      }
    });
  });

  // Core redraws a post only when the post changes; the visible number also depends on the hidden
  // events, which arrive later — so it is one of the things the post's subtree checks.
  extend(CommentPost.prototype, 'oninit', function () {
    this.subtree?.check(() => visibleNumber(this.attrs.post));
  });

  // "№ 01" — the post number in the portal's numbering style, linking to the post. Hidden system
  // events (pinning) leave no gaps in what readers see; the link keeps the post's real number.
  extend(CommentPost.prototype, 'headerItems', function (items) {
    const post = this.attrs.post;
    const discussion = post.discussion();
    if (!discussion) return;
    items.add('tp-number', m(Link, {
      className: "TpPostNumber",
      href: app.route.discussion(discussion, post.number())
    }, app.translator.trans('tarotportal-theme.forum.discussion.post_number', {
      number: String(visibleNumber(post)).padStart(2, '0')
    })), -100);
  });

  // Sidebar actions, one job each (phones: one compact row, see phone.less):
  //   [ ↩ Ответить ]        core's own reply control (guests: log in; no permission: disabled) —
  //                          it brings the reader to the reply form under the posts;
  //   [ ☆ Подписка ][▾]     flarum/subscriptions, unchanged;
  //   [ ⚙ Настройки темы ]  core's discussion menu (rename, tags, pin, lock, delete… and whatever
  //                          extensions add), shown only when it has something for this reader.
  // Core's split button, whose arrow held that menu, is gone: replying never hides other actions.
  extend(DiscussionPage.prototype, 'sidebarItems', function (items) {
    const discussion = this.discussion;
    if (!discussion || !items.has('controls')) return;
    const userControls = DiscussionControls.userControls(discussion, this);
    const reply = userControls.has('reply') ? userControls.get('reply') : null;
    if (reply?.attrs) {
      reply.attrs.className = classList(reply.attrs.className, 'Button Button--primary TpReplyAction');
      items.setContent('controls', reply);
    } else {
      items.remove('controls');
    }
    const controls = DiscussionControls.controls(discussion, this);
    for (const name of ['reply', 'subscription']) if (controls.has(name)) controls.remove(name);
    const menu = controls.toArray().filter(item => item?.tag !== Separator);
    if (!menu.length) return;
    items.add('tp-topic-actions', m(Dropdown, {
      className: "TpTopicActions",
      buttonClassName: "Button",
      menuClassName: "Dropdown-menu--right",
      icon: "fas fa-cog",
      label: app.translator.trans('tarotportal-theme.forum.discussion.topic_actions'),
      accessibleToggleLabel: app.translator.trans('tarotportal-theme.forum.discussion.topic_actions')
    }, controls.toArray()), 70);
  });

  // Like (flarum/likes): one compact control — [thumb-up] N.
  //   - the icon is flarum/likes' own button (its click handler, permission and state): outline when
  //     not liked, solid gold when liked; its name is the tooltip, never a line of text;
  //   - N is the post's likesCount (flarum/likes), hidden at 0. A click adjusts it at once (as
  //     flarum/likes does for its likes list); the saved post brings back the server's count;
  //   - N opens flarum/likes' list of who liked the post, for those allowed to see users;
  //   - the number keeps a two-digit slot even at 0, so the icon never moves in the right-aligned row.
  // Readers who cannot like (guests, no permission) see a plain icon and N when N > 0.
  extend(CommentPost.prototype, 'actionItems', function (items) {
    const post = this.attrs.post;
    if (post.isHidden()) return;
    const count = Number(post.attribute('likesCount')) || 0;
    const button = items.has('like') ? items.get('like') : null;
    const canLike = !!button?.attrs;
    if (!canLike && !count) return;
    if (canLike) {
      const user = app.session.user;
      const liked = !!user && (post.likes() || []).some(u => u === user);
      const label = app.translator.trans(`tarotportal-theme.forum.discussion.${liked ? 'unlike' : 'like'}`, {}, true);
      const toggle = button.attrs.onclick;
      button.attrs.icon = liked ? 'fas fa-thumbs-up' : 'far fa-thumbs-up';
      button.attrs.className = classList(button.attrs.className, 'Button--icon TpLike', liked && 'TpLike--liked');
      button.attrs['aria-label'] = label;
      button.attrs['aria-pressed'] = liked ? 'true' : 'false';
      button.attrs.title = label;
      button.attrs.onclick = e => {
        toggle(e);
        post.pushAttributes({
          likesCount: Math.max(0, count + (liked ? -1 : 1))
        });
      };
      button.children = [];
    }
    const listLabel = app.translator.trans('tarotportal-theme.forum.discussion.likes_list', {}, true);
    const counter = !count ? m("span", {
      className: "TpLikeCount TpLikeCount--empty",
      "aria-hidden": "true"
    }) : app.forum.attribute('canSearchUsers') ? m("button", {
      type: "button",
      className: "TpLikeCount",
      title: listLabel,
      "aria-label": `${listLabel}: ${count}`,
      onclick: () => {
        // flarum/likes loads after this theme: its modal is looked up when needed
        const PostLikesModal = flarum.reg.get('flarum-likes', 'forum/components/PostLikesModal');
        app.modal.show(PostLikesModal.default || PostLikesModal, {
          post
        });
      }
    }, count) : m("span", {
      className: "TpLikeCount"
    }, count);
    const control = m("span", {
      className: classList('TpLikeControl', !canLike && 'TpLikeControl--static')
    }, canLike ? button : m(Icon, {
      name: "far fa-thumbs-up",
      className: "TpLikeIcon"
    }), counter);
    if (canLike) items.setContent('like', control);else items.add('like', control);
  });

  // "… оценили это" (flarum/likes) names the other people who liked the post; the reader's own like
  // is shown by the icon, so "Вы" is left out, and the line disappears when the reader is the only one.
  // Same names, limit, "ещё N" link to the full list (PostLikesModal) and translations as flarum/likes.
  // Where the like count opens that list (members allowed to see users), the line is not shown at all.
  extend(CommentPost.prototype, 'footerItems', function (items) {
    if (items.has('liked') && app.forum.attribute('canSearchUsers')) {
      items.remove('liked');
      return;
    }
    const user = app.session.user;
    if (!items.has('liked') || !user) return;
    const post = this.attrs.post;
    const likes = post.likes() || [];
    if (!likes.includes(user)) return;
    const others = likes.filter(u => u !== user);
    const total = Math.max((post.likesCount() || likes.length) - 1, others.length);
    if (!total) {
      items.remove('liked');
      return;
    }
    const limit = 4;
    const overLimit = total > limit;
    const names = others.slice(0, overLimit ? limit - 1 : limit).map(u => m(Link, {
      href: app.route.user(u)
    }, username(u)));
    if (overLimit) {
      const label = app.translator.trans('flarum-likes.forum.post.others_link', {
        count: total - names.length
      });
      names.push(app.forum.attribute('canSearchUsers') ? m(Button, {
        className: "Button Button--ua-reset Button--text",
        onclick: e => {
          e.preventDefault();
          // flarum/likes loads after this theme: its modal is looked up when needed
          const PostLikesModal = flarum.reg.get('flarum-likes', 'forum/components/PostLikesModal');
          app.modal.show(PostLikesModal.default || PostLikesModal, {
            post
          });
        }
      }, label) : m("span", null, label));
    }
    items.setContent('liked', m("div", {
      className: "Post-likedBy"
    }, m(Icon, {
      name: "far fa-thumbs-up"
    }), app.translator.trans('flarum-likes.forum.post.liked_by_text', {
      count: names.length,
      users: punctuateSeries(names)
    })));
  });

  // Sidebar: after reply controls and the scrubber, the subsections of the discussion's section.
  extend(DiscussionPage.prototype, 'sidebarItems', function (items) {
    const discussion = this.discussion;
    if (!discussion) return;
    const {
      section,
      subsection
    } = discussionPlacement(discussion.tags() || []);
    if (!section) return;
    const children = subsections(section);
    items.add('tp-section', m("div", {
      className: "TpSideBlock"
    }, m("p", {
      className: "TpEyebrow TpEyebrow--muted TpRule"
    }, app.translator.trans('tarotportal-theme.forum.discussion.section_heading', {
      name: section.name()
    })), m("ul", {
      className: "TpSideNav"
    }, (children.length ? children : [section]).map(tag => m("li", null, m(Link, {
      className: classList({
        active: tag === subsection
      }),
      href: app.route.tag(tag)
    }, tag.name()))))), -200);
  });
}